import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import {
  Truck, Heart, MapPin, Star, ShoppingBag, ArrowRight, ArrowLeft,
  Clock, Pencil, Palette, MessageSquare, Award, Users, X,
  Upload, Loader2, Check, Zap, Eye, Flame
} from "lucide-react";

/* ═══ DESIGN TOKENS ═══ */
const C = {
  bg:      "#070707",
  bg2:     "#11100E",
  card:    "#181614",
  paper:   "#F5F2EB",
  text:    "#F5F2EB",
  text2:   "#B5B0A7",
  gold:    "#C9A055",
  goldBr:  "#E0BC72",
  red:     "#FF3B30",
  cyan:    "#00E5FF",
  bSub:    "rgba(245,242,235,0.10)",
  bGold:   "rgba(201,160,85,0.35)",
};
const F = {
  brush: "'Permanent Marker', cursive",
  cond:  "'Saira Condensed', sans-serif",
  hand:  "'Caveat', cursive",
  sans:  "'DM Sans', 'Outfit', sans-serif",
};

/* ═══ REUSABLE VISIBILITY HOOK ═══ */
function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, v];
}

/* ═══ ANIMATED COUNTER ═══ */
function useCountUp(target, duration = 1800, trigger = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger || target === 0) return;
    let start = 0;
    const step = Math.max(1, Math.ceil(target / (duration / 50)));
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(id); }
      else setVal(start);
    }, 50);
    return () => clearInterval(id);
  }, [target, trigger, duration]);
  return val;
}

/* ═══ INK SPLATTER SVG ═══ */
const InkSplat = ({ className = "", style = {} }) => (
  <svg viewBox="0 0 200 200" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    <circle cx="100" cy="100" r="60" fill="currentColor" opacity="0.7"/>
    <circle cx="140" cy="70" r="20" fill="currentColor" opacity="0.5"/>
    <circle cx="60" cy="130" r="25" fill="currentColor" opacity="0.6"/>
    <circle cx="155" cy="130" r="12" fill="currentColor" opacity="0.4"/>
    <circle cx="50" cy="65" r="15" fill="currentColor" opacity="0.45"/>
    <circle cx="120" cy="155" r="10" fill="currentColor" opacity="0.35"/>
    <ellipse cx="100" cy="100" rx="80" ry="40" fill="currentColor" opacity="0.15"/>
  </svg>
);

/* ═══ MARKER STROKE LINE ═══ */
const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{
    width, height, background: color, borderRadius: "3px",
    transform: "rotate(-0.8deg)", opacity: 0.85,
    boxShadow: `0 0 12px ${color}40`,
  }} />
);

/* ════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
════════════════════════════════════════════════════════ */
export default function MarkerCollection() {
  useSEO("marker-collection");
  const [artworks, setArtworks] = useState([]);
  const [settings, setSettings] = useState({});
  const [queue, setQueue] = useState([]);
  const [discount, setDiscount] = useState({ enabled: false, per_item: 0, min_qty: 2 });
  const [activeTag, setActiveTag] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState({});

  useEffect(() => {
    Promise.all([
      http.get("/marker-collection/public/products"),
      http.get("/marker-collection/public/settings"),
      http.get("/marker-collection/public/queue"),
      http.get("/marker-collection/public/discount-info"),
      http.get("/marker-collection/public/pages"),
    ]).then(([a, s, q, d, p]) => {
      setArtworks(a.data || []);
      setSettings(s.data || {});
      setQueue(q.data || []);
      setDiscount(d.data || {});
      setPages(p.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Exclude auto-synced linked prints from the storefront grid
  const displayArtworks = useMemo(() => artworks.filter(a => !a.linked_parent_id), [artworks]);

  // Map parent artwork ID → print price for "Prints from $X" labels
  const printPriceMap = useMemo(() => {
    const m = {};
    displayArtworks.forEach(a => {
      if (a.linked_print_id && a.print_price) m[a.id] = a.print_price;
    });
    return m;
  }, [displayArtworks]);

  const allTags = ["ALL"];
  const tagSet = new Set();
  displayArtworks.forEach(a => {
    if (a.art_type) tagSet.add(a.art_type);
    (a.tags || []).forEach(t => tagSet.add(t));
  });
  tagSet.forEach(t => allTags.push(t));

  const filtered = activeTag === "ALL" ? displayArtworks
    : displayArtworks.filter(a => a.art_type === activeTag || (a.tags || []).includes(activeTag));
  const originals = displayArtworks.filter(a => a.art_type === "Original");
  const s = settings;

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <div className="relative">
          <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, overflowX: "hidden" }}>
        <style>{`
          @keyframes mc-float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
          @keyframes mc-pulse-ring { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.8); opacity: 0; } }
          @keyframes mc-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
          @keyframes mc-draw { from { stroke-dashoffset: 800; } to { stroke-dashoffset: 0; } }
          @keyframes mc-grain { 0%,100% { transform: translate(0,0); } }
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          .mc-card-dramatic { transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
          .mc-card-dramatic:hover { transform: translateY(-8px) scale(1.02); }
          .mc-card-dramatic:hover .mc-card-overlay { opacity: 1; }
          .mc-card-dramatic:hover .mc-card-image { transform: scale(1.08); }
          .mc-shimmer-gold { background: linear-gradient(90deg, ${C.gold}, ${C.goldBr}, ${C.gold}); background-size: 200% auto; animation: mc-shimmer 3s linear infinite; }
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        {/* ════════════════════════════════════════
            HERO — INK SPLATTER RAW STUDIO
        ════════════════════════════════════════ */}
        <HeroSection artworks={artworks} settings={s} />

        {/* ════════════════════════════════════════
            TRUST BAR
        ════════════════════════════════════════ */}
        <TrustBar />

        {/* ════════════════════════════════════════
            SHOP THE COLLECTION
        ════════════════════════════════════════ */}
        <ShopSection
          filtered={filtered}
          allTags={allTags}
          activeTag={activeTag}
          setActiveTag={setActiveTag}
          artworks={displayArtworks}
          printPriceMap={printPriceMap}
          galleryEnabled={pages?.gallery?.enabled !== false}
        />

        {/* ════════════════════════════════════════
            BUY MORE SAVE MORE
        ════════════════════════════════════════ */}
        {discount.enabled && <DiscountBanner discount={discount} />}

        {/* ════════════════════════════════════════
            ONE DRAWING. ONE ORIGINAL.
        ════════════════════════════════════════ */}
        {originals.length > 0 && <OriginalsSection originals={originals} settings={s} setActiveTag={setActiveTag} originalsEnabled={pages?.originals?.enabled !== false} />}

        {/* ════════════════════════════════════════
            ANIMATED QUEUE COUNTER — STANDALONE
        ════════════════════════════════════════ */}
        <QueueCounterSection queue={queue} />

        {/* ════════════════════════════════════════
            CHALLENGE BOARD CTA
        ════════════════════════════════════════ */}
        <ChallengeBoardCTA />

        {/* ════════════════════════════════════════
            COMMISSION + QUEUE
        ════════════════════════════════════════ */}
        <CommissionQueueSection settings={s} queue={queue} commissionsEnabled={pages?.commissions?.enabled !== false} />

        {/* ════════════════════════════════════════
            ART FOR A CAUSE — Nonprofit Partnership
        ════════════════════════════════════════ */}
        <ArtForACauseSection />

        {/* ════════════════════════════════════════
            MEET THE ARTIST
        ════════════════════════════════════════ */}
        <ArtistSection settings={s} aboutEnabled={pages?.about?.enabled !== false} processEnabled={pages?.process?.enabled !== false} />

        {/* ════════════════════════════════════════
            EMAIL SIGNUP
        ════════════════════════════════════════ */}
        <EmailSection settings={s} />

        {/* ════════════════════════════════════════
            FOOTER
        ════════════════════════════════════════ */}
        <MCFooter pages={pages} />
      </div>
    </Layout>
  );
}


/* ════════════════════════════════════════════════════════
   HERO SECTION
════════════════════════════════════════════════════════ */
function HeroSection({ artworks, settings }) {
  const [ref, vis] = useVisible(0.1);
  const featured = artworks[0];

  return (
    <section ref={ref} className="relative overflow-hidden mc-grit" style={{ background: C.bg, minHeight: "600px" }} data-testid="mc-hero">
      {/* Ink splatter decorations */}
      <InkSplat className="absolute top-[-40px] left-[-60px] w-64 h-64 opacity-[0.03] pointer-events-none" style={{ color: C.gold }} />
      <InkSplat className="absolute bottom-[-30px] right-[-40px] w-48 h-48 opacity-[0.04] pointer-events-none" style={{ color: C.red }} />

      {/* Animated marker line SVG */}
      <svg className="absolute top-20 left-10 w-[300px] h-[200px] pointer-events-none opacity-[0.08]" viewBox="0 0 300 200">
        <path d="M10 100 Q80 30 150 100 T290 100" fill="none" stroke={C.gold} strokeWidth="3"
          strokeDasharray="800" style={{ animation: vis ? "mc-draw 2.5s ease forwards" : "none" }} />
      </svg>

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-12 items-center">
          {/* LEFT — Title & CTAs */}
          <div>
            {/* Eyebrow */}
            <div className={`mb-4 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5"
                style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}>
                HAND-DRAWN ART
              </span>
            </div>

            {/* Main title */}
            <h1 className={`text-6xl sm:text-7xl lg:text-[6.5rem] font-black uppercase leading-[0.85] tracking-tight mb-5 transition-all duration-1000 delay-200 ${vis ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}
              style={{ fontFamily: F.brush, color: C.text }}
              data-testid="mc-hero-title">
              MARKER<br/>
              <span style={{ color: C.gold, textShadow: `0 0 60px ${C.gold}30` }}>COLLECTION</span>
            </h1>

            {/* Marker stroke underline */}
            <div className={`transition-all duration-700 delay-500 ${vis ? "opacity-100 w-48" : "opacity-0 w-0"}`}>
              <MarkerStroke width="100%" />
            </div>

            {/* Tagline */}
            <p className={`mt-5 text-sm tracking-[0.3em] uppercase font-bold transition-all duration-700 delay-500 ${vis ? "opacity-100" : "opacity-0"}`}
              style={{ fontFamily: F.sans, color: C.gold }}>
              MADE BY HAND. MARK BY MARK.
            </p>

            {/* Handwritten quote */}
            <p className={`mt-4 text-2xl leading-snug italic transition-all duration-700 delay-700 ${vis ? "opacity-100" : "opacity-0"}`}
              style={{ fontFamily: F.hand, color: `${C.text}60` }}>
              "Just a blank page, a marker,<br/>and an idea that won't shut up."
            </p>

            {/* CTAs */}
            <div className={`flex flex-wrap gap-4 mt-8 transition-all duration-700 delay-[900ms] ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <a href="#shop" className="group inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
                style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
                data-testid="mc-hero-shop-btn">
                SHOP THE COLLECTION <ArrowRight size={15} />
              </a>
              <a href="#commission" className="inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-bold border-2 transition-all duration-300 hover:bg-white/5"
                style={{ fontFamily: F.sans, borderColor: C.text, color: C.text }}
                data-testid="mc-hero-commission-btn">
                REQUEST A COMMISSION
              </a>
            </div>
          </div>

          {/* RIGHT — Featured artwork floating card */}
          <div className={`relative transition-all duration-1000 delay-500 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
            {featured?.main_image ? (
              <Link to={`/marker-collection/${featured.slug}`} className="block relative group">
                <div className="relative" style={{ animation: "mc-float 4s ease-in-out infinite" }}>
                  {/* Glow behind */}
                  <div className="absolute -inset-6 blur-3xl opacity-20 pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${C.gold} 0%, transparent 70%)` }} />
                  {/* Frame */}
                  <div className="relative border-2 p-1" style={{ borderColor: C.bGold, background: C.card }}>
                    <img src={resolveImage(featured.main_image)} alt={featured.title}
                      className="w-full aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                    {/* Badge */}
                    <div className="absolute top-3 left-3 px-3 py-1 text-[9px] tracking-[0.3em] uppercase font-bold"
                      style={{ fontFamily: F.sans, background: featured.art_type === "Original" ? C.red : C.gold, color: featured.art_type === "Original" ? "#fff" : C.bg }}>
                      {featured.art_type || "ARTWORK"}
                    </div>
                    {/* Price tag */}
                    <div className="absolute bottom-3 right-3 px-4 py-2" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}>
                      <span className="text-lg font-black" style={{ fontFamily: F.cond, color: C.text }}>${Number(featured.price || 0).toFixed(0)}</span>
                    </div>
                  </div>
                  {/* Corner ticks */}
                  <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2" style={{ borderColor: C.gold }} />
                  <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2" style={{ borderColor: C.gold }} />
                </div>
              </Link>
            ) : (
              <div className="aspect-[3/4] flex items-center justify-center opacity-10" style={{ background: C.card }}>
                <Palette size={80} style={{ color: C.gold }} />
              </div>
            )}

            {/* Side text */}
            <div className="absolute -right-2 top-6 hidden xl:block" style={{ writingMode: "vertical-rl", fontFamily: F.hand, color: `${C.gold}40`, fontSize: "16px" }}>
              ART · PEOPLE · ANIMALS · PLACES · IDEAS
            </div>
          </div>
        </div>
      </div>

      {/* Bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] mc-shimmer-gold" />
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   TRUST BAR
════════════════════════════════════════════════════════ */
function TrustBar() {
  return (
    <section style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-trust-bar">
      <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          [Truck, "Fast & Secure", "Shipping"],
          [Heart, "Supports a", "Small Business"],
          [MapPin, "East Tennessee", "Born & Drawn"],
          [Star, "Good Vibes", "Always"],
        ].map(([Icon, l1, l2], i) => (
          <div key={i} className="flex items-center gap-3 justify-center group cursor-default">
            <div className="w-10 h-10 flex items-center justify-center border transition-all duration-300 group-hover:shadow-[0_0_15px_rgba(201,160,85,0.25)]"
              style={{ borderColor: C.bGold, background: `${C.gold}08` }}>
              <Icon size={18} style={{ color: C.gold }} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-bold" style={{ fontFamily: F.sans, color: C.text }}>{l1}</div>
              <div className="text-[10px] uppercase tracking-[0.12em]" style={{ fontFamily: F.sans, color: C.text2 }}>{l2}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   SHOP SECTION
════════════════════════════════════════════════════════ */
function ShopSection({ filtered, allTags, activeTag, setActiveTag, artworks, printPriceMap, galleryEnabled }) {
  const [ref, vis] = useVisible(0.08);
  const [sortBy, setSortBy] = useState("default");
  const [showCount, setShowCount] = useState(8);

  // Reset page when filter changes
  useEffect(() => { setShowCount(8); }, [activeTag]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "price-low") arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === "price-high") arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === "originals") arr.sort((a, b) => (a.art_type === "Original" ? 0 : 1) - (b.art_type === "Original" ? 0 : 1));
    return arr;
  }, [filtered, sortBy]);

  const displayed = sorted.slice(0, showCount);
  const hasMore = sorted.length > showCount;

  return (
    <section ref={ref} id="shop" className="relative mc-grit" style={{ background: C.bg2 }} data-testid="mc-shop">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-24 relative z-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div className={`transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-bold mb-2" style={{ fontFamily: F.sans, color: C.red }}>
              ORIGINAL ARTWORK & HIGH QUALITY PRINTS
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[0.9]"
              style={{ fontFamily: F.cond, color: C.text }}>
              SHOP THE<br/>COLLECTION
            </h2>
          </div>
          <p className={`text-2xl sm:text-3xl transition-all duration-700 delay-300 ${vis ? "opacity-100" : "opacity-0"}`}
            style={{ fontFamily: F.hand, color: C.gold }}>
            Find Your Piece.
          </p>
        </div>

        {/* Filters + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-6 sm:mb-8" data-testid="mc-shop-controls">
          {/* Tag filters — horizontal scroll on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap w-full sm:w-auto no-scrollbar" data-testid="mc-tag-filters"
            style={{ WebkitOverflowScrolling: "touch" }}>
            {allTags.map(tag => {
              const count = tag === "ALL" ? artworks.length
                : artworks.filter(a => a.art_type === tag || (a.tags || []).includes(tag)).length;
              return (
                <button key={tag} onClick={() => setActiveTag(tag)}
                  className="flex items-center gap-1.5 px-3 sm:px-5 py-2 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.2em] uppercase font-bold transition-all duration-300 flex-shrink-0"
                  style={{
                    fontFamily: F.sans,
                    borderRadius: "999px",
                    border: `2px solid ${activeTag === tag ? C.gold : C.bSub}`,
                    background: activeTag === tag ? C.gold : "transparent",
                    color: activeTag === tag ? C.bg : C.text2,
                  }}
                  data-testid={`mc-tag-${tag}`}>
                  {tag}
                  <span className="text-[7px] sm:text-[8px] px-1.5 py-0.5 rounded-full"
                    style={{ background: activeTag === tag ? "rgba(0,0,0,0.2)" : `${C.gold}15`, color: activeTag === tag ? C.bg : C.gold }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sort + count */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-[9px] sm:text-[10px] tracking-[0.15em] uppercase"
              style={{ fontFamily: F.sans, color: C.text2 }}>
              {sorted.length} piece{sorted.length !== 1 ? "s" : ""}
            </span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="text-[9px] sm:text-[10px] tracking-[0.12em] uppercase px-3 py-2 outline-none cursor-pointer"
              style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.bSub}`, color: C.text2, appearance: "auto" }}
              data-testid="mc-sort-select">
              <option value="default">Featured</option>
              <option value="price-low">Price: Low → High</option>
              <option value="price-high">Price: High → Low</option>
              <option value="originals">Originals First</option>
            </select>
          </div>
        </div>

        {/* Product grid — 2-col mobile, 3-col lg, 4-col xl */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-8" data-testid="mc-product-grid">
          {displayed.map((art, i) => (
            <DramaticArtCard key={art.id} art={art} index={i} vis={vis} printPrice={printPriceMap[art.id]} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16">
              <Palette size={40} className="mx-auto mb-4" style={{ color: `${C.gold}30` }} />
              <p style={{ fontFamily: F.sans, color: C.text2 }}>No artworks found for "{activeTag}"</p>
            </div>
          )}
        </div>

        {/* Show More */}
        {hasMore && (
          <div className="text-center mt-8 sm:mt-10" data-testid="mc-show-more-wrapper">
            <button onClick={() => setShowCount(prev => prev + 8)}
              className="group inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-3.5 text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
              style={{ fontFamily: F.sans, border: `2px solid ${C.bGold}`, color: C.gold, background: `${C.gold}08` }}
              data-testid="mc-show-more-btn">
              SHOW MORE
              <span className="text-[8px] sm:text-[9px] px-2 py-0.5 rounded-full" style={{ background: `${C.gold}15`, color: C.gold }}>
                {sorted.length - showCount} more
              </span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-2 text-[9px] sm:text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>
              Showing {displayed.length} of {sorted.length}
            </p>
          </div>
        )}

        {!hasMore && sorted.length > 8 && (
          <p className="text-center mt-6 text-[9px] sm:text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>
            Showing all {sorted.length} pieces
          </p>
        )}

        {/* Link to Gallery page */}
        {galleryEnabled && (
          <div className="text-center mt-8">
            <Link to="/marker-collection/gallery" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold hover:gap-4 transition-all"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-shop-gallery-link">
              VIEW FULL GALLERY <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

/* ═══ DRAMATIC ART CARD ═══ */
function DramaticArtCard({ art, index, vis, printPrice }) {
  const isSold = art.original_status === "sold" || (art.quantity != null && art.quantity <= 0);
  return (
    <Link to={`/marker-collection/${art.slug}`}
      className="mc-card-dramatic block relative group"
      style={{
        opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(30px)",
        transition: `all 0.6s cubic-bezier(0.25,0.46,0.45,0.94) ${100 + index * 60}ms`,
      }}
      data-testid={`mc-card-${art.slug}`}>
      <div className="relative overflow-hidden" style={{ background: C.card, border: `1px solid ${C.bSub}` }}>
        {/* Image */}
        <div className="overflow-hidden">
          {art.main_image ? (
            <img src={resolveImage(art.main_image)} alt={art.title}
              className="mc-card-image w-full aspect-[3/4] object-cover transition-transform duration-700" loading="lazy" />
          ) : (
            <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: C.bg2 }}>
              <Palette size={32} style={{ color: `${C.gold}20` }} />
            </div>
          )}
        </div>

        {/* Type badge */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 px-2 sm:px-3 py-0.5 sm:py-1 text-[7px] sm:text-[8px] tracking-[0.25em] sm:tracking-[0.3em] uppercase font-bold z-10"
          style={{ fontFamily: F.sans, background: art.art_type === "Original" ? C.red : C.gold, color: art.art_type === "Original" ? "#fff" : C.bg }}>
          {art.art_type || "ART"}
        </div>

        {/* Charity badge */}
        {art.charity?.enabled && (
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10 flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1"
            style={{ background: "rgba(0,0,0,0.7)", border: "1px solid rgba(74,222,128,0.3)", backdropFilter: "blur(6px)" }}
            data-testid={`mc-card-charity-${art.slug}`}>
            <Heart size={8} fill="#4ade80" color="#4ade80" />
            <span className="text-[6px] sm:text-[8px] tracking-[0.15em] sm:tracking-[0.2em] uppercase font-bold" style={{ fontFamily: F.sans, color: "#4ade80" }}>CAUSE</span>
          </div>
        )}

        {/* Sale badge */}
        {art.on_sale && art.sale_price > 0 && art.sale_price < art.price && !art.charity?.enabled && (
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10 px-1.5 sm:px-2.5 py-0.5 sm:py-1"
            style={{ background: "#FF3B30" }}
            data-testid={`mc-card-sale-${art.slug}`}>
            <span className="text-[6px] sm:text-[8px] tracking-[0.15em] sm:tracking-[0.2em] uppercase font-bold" style={{ fontFamily: F.sans, color: "#fff" }}>SALE</span>
          </div>
        )}

        {/* Hover overlay — hidden on mobile */}
        <div className="mc-card-overlay absolute inset-0 opacity-0 transition-opacity duration-400 hidden sm:flex flex-col items-center justify-end p-5"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)" }}>
          <div className="flex items-center gap-3 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold"
            style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}>
            <Eye size={14} /> VIEW PIECE
          </div>
        </div>

        {/* Sold overlay */}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center z-20"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(2px)" }}>
            <span className="text-xs sm:text-sm tracking-[0.35em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD</span>
          </div>
        )}

        {/* Mobile price overlay — bottom-right on image for quick scanning */}
        <div className="sm:hidden absolute bottom-2 right-2 px-2 py-1 z-10"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
          {art.on_sale && art.sale_price > 0 && art.sale_price < art.price ? (
            <span className="flex items-baseline gap-1">
              <span className="text-sm font-black" style={{ fontFamily: F.cond, color: "#FF3B30" }}>
                ${Number(art.sale_price).toFixed(0)}
              </span>
              <span className="text-[9px] font-bold line-through" style={{ fontFamily: F.cond, color: "#999" }}>
                ${Number(art.price || 0).toFixed(0)}
              </span>
            </span>
          ) : (
            <span className="text-sm font-black" style={{ fontFamily: F.cond, color: isSold ? C.text2 : C.gold }}>
              ${Number(art.price || 0).toFixed(0)}
            </span>
          )}
        </div>
      </div>

      {/* Info below image */}
      <div className="mt-1.5 sm:mt-3 flex items-end justify-between gap-1 sm:gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] sm:text-base font-bold truncate leading-tight" style={{ fontFamily: F.sans, color: C.text }}>{art.title}</p>
          {printPrice ? (
            <p className="text-[8px] sm:text-[10px] tracking-[0.12em] uppercase mt-0.5" style={{ fontFamily: F.sans, color: C.gold }}
              data-testid={`mc-card-prints-from-${art.slug}`}>
              + prints from ${Number(printPrice).toFixed(0)}
            </p>
          ) : art.quantity != null && art.quantity > 0 && art.quantity <= 5 ? (
            <p className="text-[8px] sm:text-[10px] tracking-[0.15em] uppercase mt-0.5" style={{ fontFamily: F.sans, color: C.red }}>Only {art.quantity} left</p>
          ) : null}
        </div>
        {/* Price — visible on desktop, hidden on mobile (shown on image instead) */}
        {art.on_sale && art.sale_price > 0 && art.sale_price < art.price ? (
          <span className="hidden sm:flex items-baseline gap-1.5 flex-shrink-0">
            <span className="text-xl font-black" style={{ fontFamily: F.cond, color: "#FF3B30" }}>
              ${Number(art.sale_price).toFixed(0)}
            </span>
            <span className="text-sm font-bold line-through" style={{ fontFamily: F.cond, color: "#999" }}>
              ${Number(art.price).toFixed(0)}
            </span>
          </span>
        ) : (
          <span className="hidden sm:block text-xl font-black flex-shrink-0" style={{ fontFamily: F.cond, color: isSold ? C.text2 : C.gold }}>
            ${Number(art.price || 0).toFixed(0)}
          </span>
        )}
      </div>
    </Link>
  );
}


/* ════════════════════════════════════════════════════════
   DISCOUNT BANNER
════════════════════════════════════════════════════════ */
function DiscountBanner({ discount }) {
  const [ref, vis] = useVisible(0.15);
  return (
    <section ref={ref} className="relative overflow-hidden mc-grit" style={{ background: C.bg }} data-testid="mc-discount-section">
      <div className="absolute top-0 left-0 right-0 h-[2px] mc-shimmer-gold" />
      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center gap-10 relative z-10">
        <div className={`flex-shrink-0 transition-all duration-700 ${vis ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
          <h3 className="text-5xl sm:text-6xl font-black leading-[0.9]"
            style={{ fontFamily: F.brush, color: C.gold, textShadow: `0 0 40px ${C.gold}20` }}>
            BUY MORE.<br/>SAVE MORE.
          </h3>
        </div>
        <div className="flex flex-wrap gap-5 flex-1 justify-center">
          {[
            { qty: "2 PRINTS", icon: "✌️" },
            { qty: "3 PRINTS", icon: "🤟" },
            { qty: "4+ PRINTS", icon: "🔥" },
          ].map((tier, i) => (
            <div key={i} className={`text-center px-8 py-5 transition-all duration-500 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: `${300 + i * 150}ms`, border: `2px solid ${C.bGold}`, background: `${C.gold}06` }}>
              <div className="text-2xl mb-1">{tier.icon}</div>
              <div className="text-base font-black tracking-[0.1em]" style={{ fontFamily: F.cond, color: C.text }}>{tier.qty}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold mt-1" style={{ fontFamily: F.sans, color: C.gold }}>
                SAVE ${Number(discount.per_item).toFixed(0)} EACH
              </div>
            </div>
          ))}
        </div>
        <p className="text-xl text-right hidden lg:block" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>
          More Art.<br/>More Savings.<br/>More Good Vibes. →
        </p>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[2px] mc-shimmer-gold" />
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   ORIGINALS SECTION
════════════════════════════════════════════════════════ */
function OriginalsSection({ originals, settings, setActiveTag, originalsEnabled }) {
  const [ref, vis] = useVisible(0.12);
  const feat = originals[0];

  return (
    <section ref={ref} className="relative mc-grit" style={{ background: C.bg2 }} data-testid="mc-originals">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className={`relative transition-all duration-1000 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
            <div className="absolute top-4 left-4 z-20 px-4 py-1.5 text-[10px] tracking-[0.25em] uppercase font-black"
              style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}>ORIGINAL — 1 OF 1</div>
            {feat?.main_image ? (
              <Link to={`/marker-collection/${feat.slug}`} className="block relative group">
                <div className="border-[6px] shadow-[0_20px_60px_rgba(0,0,0,0.5)]" style={{ borderColor: "#2a2520", background: "#1a1510" }}>
                  <img src={resolveImage(feat.main_image)} alt={feat.title}
                    className="w-full aspect-[4/5] object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2" style={{ borderColor: C.gold }} />
                <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2" style={{ borderColor: C.gold }} />
              </Link>
            ) : (
              <div className="aspect-[4/5] border-[6px] flex items-center justify-center" style={{ borderColor: "#2a2520", background: "#1a1510" }}>
                <Palette size={64} style={{ color: `${C.gold}20` }} />
              </div>
            )}
          </div>

          {/* Text */}
          <div className={`transition-all duration-700 delay-300 ${vis ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <h2 className="text-5xl sm:text-6xl font-black leading-[0.9] mb-5"
              style={{ fontFamily: F.cond, color: C.text }}>
              {settings.originals_heading || "ONE DRAWING.\nONE ORIGINAL."}
            </h2>
            <MarkerStroke width="60px" className="mb-6" />
            <p className="text-base leading-relaxed mb-6 max-w-lg"
              style={{ fontFamily: F.sans, color: C.text2 }}>
              {settings.originals_text || "The actual hand-drawn originals. One of one. Signed, titled, and comes with a Certificate of Authenticity. When it's gone, it's gone."}
            </p>
            <button onClick={() => { setActiveTag("Original"); document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
              style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}
              data-testid="mc-view-originals-btn">
              VIEW ORIGINALS <ArrowRight size={15} />
            </button>
            {originalsEnabled && (
              <Link to="/marker-collection/originals" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold hover:gap-4 transition-all ml-2"
                style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-originals-page-link">
                SEE ALL ORIGINALS <ArrowRight size={14} />
              </Link>
            )}
            <p className="mt-10 text-2xl leading-snug" style={{ fontFamily: F.hand, color: `${C.gold}50` }}>
              "Not reproductions.<br/>Pieces of me."<br/>— Mick
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   ★ ANIMATED QUEUE COUNTER — STANDALONE SHOWSTOPPER ★
════════════════════════════════════════════════════════ */
function QueueCounterSection({ queue }) {
  const [ref, vis] = useVisible(0.2);
  const inProgress = queue.filter(q => q.status === "in_progress").length;
  const total = queue.length;
  const animTotal = useCountUp(total, 1500, vis);
  const animProg = useCountUp(inProgress, 1200, vis);

  return (
    <section ref={ref} className="relative overflow-hidden" style={{ background: C.bg }} data-testid="mc-animated-queue-counter">
      {/* Top gold line */}
      <div className="h-[3px] mc-shimmer-gold" />

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Eyebrow */}
          <div className={`mb-3 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-[10px] tracking-[0.35em] uppercase font-bold"
              style={{ fontFamily: F.sans, border: `1px solid ${C.bGold}`, color: C.gold }}>
              <Zap size={12} /> LIVE STUDIO UPDATE
            </span>
          </div>

          {/* Big number */}
          <div className={`relative my-6 transition-all duration-1000 delay-200 ${vis ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}>
            {/* Pulse rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-40 h-40 rounded-full border-2" style={{ borderColor: `${C.gold}30`, animation: "mc-pulse-ring 2.5s ease-out infinite" }} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-40 h-40 rounded-full border-2" style={{ borderColor: `${C.gold}20`, animation: "mc-pulse-ring 2.5s ease-out infinite 0.8s" }} />
            </div>

            {/* Counter number */}
            <div className="relative w-44 h-44 flex items-center justify-center"
              style={{ border: `3px solid ${C.gold}`, background: `${C.gold}08`, boxShadow: `0 0 60px ${C.gold}15, inset 0 0 40px ${C.gold}08` }}>
              <span className="text-8xl font-black" style={{ fontFamily: F.cond, color: C.gold, textShadow: `0 0 30px ${C.gold}40` }}
                data-testid="mc-queue-counter-number">
                {animTotal}
              </span>
            </div>
          </div>

          {/* Label */}
          <div className={`transition-all duration-700 delay-500 ${vis ? "opacity-100" : "opacity-0"}`}>
            <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-2"
              style={{ fontFamily: F.cond, color: C.text }}>
              PROJECTS IN THE QUEUE
            </h3>
            <p className="text-sm max-w-md mx-auto mb-4"
              style={{ fontFamily: F.sans, color: C.text2 }}>
              Every piece is made by hand. Join the queue and watch your art come to life.
            </p>
          </div>

          {/* Status pills */}
          <div className={`flex flex-wrap justify-center gap-4 transition-all duration-700 delay-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="flex items-center gap-2 px-5 py-2.5"
              style={{ background: `${C.gold}10`, border: `1px solid ${C.bGold}` }}>
              <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: C.gold }} />
              <span className="text-sm font-bold" style={{ fontFamily: F.cond, color: C.gold }}>{animProg}</span>
              <span className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>In Progress</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-2.5"
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.bSub}` }}>
              <Clock size={14} style={{ color: C.text2 }} />
              <span className="text-sm font-bold" style={{ fontFamily: F.cond, color: C.text }}>{animTotal - animProg}</span>
              <span className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>Up Next</span>
            </div>
          </div>

          {/* CTA */}
          <div className={`mt-8 transition-all duration-700 delay-[900ms] ${vis ? "opacity-100" : "opacity-0"}`}>
            <a href="#commission" className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
              style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
              data-testid="mc-queue-counter-cta">
              GET IN LINE <ArrowRight size={14} />
            </a>
          </div>

          {/* Handwritten note */}
          <p className={`mt-6 text-xl transition-all duration-700 delay-1000 ${vis ? "opacity-100" : "opacity-0"}`}
            style={{ fontFamily: F.hand, color: `${C.gold}40` }}>
            Good things take time. Great things take a marker.
          </p>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="h-[3px] mc-shimmer-gold" />
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   COMMISSION + QUEUE
════════════════════════════════════════════════════════ */
function CommissionQueueSection({ settings, queue, commissionsEnabled }) {
  const [ref, vis] = useVisible(0.1);
  const [showForm, setShowForm] = useState(false);
  const s = settings;

  return (
    <section ref={ref} id="commission" className="relative mc-grit" style={{ background: C.bg2 }} data-testid="mc-commission-queue">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16">
          {/* LEFT — Commission */}
          <div className={`transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-5"
              style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}>
              {s.commission_badge || "COMMISSIONS OPEN"}
            </span>
            <h2 className="text-5xl sm:text-6xl font-black leading-[0.9] mb-4"
              style={{ fontFamily: F.brush, color: C.text }}>
              {s.commission_heading || "MAKE\nIT YOURS."}
            </h2>
            <MarkerStroke width="50px" className="mb-5" />
            <p className="text-sm leading-relaxed mb-6 max-w-md"
              style={{ fontFamily: F.sans, color: C.text2 }}>
              {s.commission_text || "A portrait, a place, a memory, or an idea — I'll bring it to life, mark by mark."}
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <button onClick={() => setShowForm(true)}
                className="group inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
                style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
                data-testid="mc-commission-cta">
                {s.commission_cta_label || "REQUEST A COMMISSION"} <ArrowRight size={14} />
              </button>
              {commissionsEnabled && (
                <Link to="/marker-collection/commissions" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold hover:gap-4 transition-all"
                  style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-commissions-page-link">
                  LEARN MORE <ArrowRight size={14} />
                </Link>
              )}
              <span className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                Starting at <strong style={{ color: C.gold }}>${Number(s.commission_starting_price || 300).toLocaleString()}</strong>
              </span>
            </div>

            {/* Process steps */}
            <div className="flex gap-6 mt-2">
              {[
                [MessageSquare, "SHARE", "YOUR IDEA"],
                [Users, "WE PLAN", "THE DETAILS"],
                [Palette, "I DRAW", "THE MAGIC"],
              ].map(([Icon, l1, l2], i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center" style={{ border: `1px solid ${C.bGold}`, background: `${C.gold}08` }}>
                    <Icon size={14} style={{ color: C.gold }} />
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.text }}>{l1}</div>
                    <div className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{l2}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Queue board */}
          <div className={`transition-all duration-700 delay-300 ${vis ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="p-6 border" style={{ borderColor: C.bSub, background: `${C.card}` }}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-2xl font-black tracking-tight" style={{ fontFamily: F.cond, color: C.text }}>THE QUEUE</h3>
                  <p className="text-[10px] tracking-[0.25em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>CURRENT PROJECTS</p>
                </div>
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: C.gold }}>
                  <span className="text-lg font-black" style={{ fontFamily: F.cond, color: C.bg }}>{queue.length}</span>
                </div>
              </div>

              {queue.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>Queue is open — be the first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {queue.map((item, i) => {
                    const isActive = item.status === "in_progress";
                    return (
                      <div key={item.id} className="flex items-center gap-3 px-3 py-3 border-b" style={{ borderColor: C.bSub }}
                        data-testid={`mc-queue-item-${item.id}`}>
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-sm font-black"
                          style={{ fontFamily: F.cond, background: isActive ? C.gold : "rgba(255,255,255,0.05)", color: isActive ? C.bg : C.text2 }}>
                          {isActive ? <Pencil size={14} /> : i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate" style={{ fontFamily: F.sans, color: C.text }}>{item.title}</p>
                          <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: isActive ? C.gold : C.text2 }}>
                            {item.type === "commission" ? "Custom Commission" : "Personal Project"}
                          </p>
                        </div>
                        <span className="text-[8px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 flex-shrink-0 flex items-center gap-1.5"
                          style={{ fontFamily: F.sans, background: isActive ? `${C.gold}15` : "rgba(255,255,255,0.03)", color: isActive ? C.gold : C.text2, border: `1px solid ${isActive ? C.bGold : C.bSub}` }}>
                          {isActive && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.gold }} />}
                          {isActive ? "IN PROGRESS" : "UP NEXT"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="mt-5 text-lg" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>Good things take time.</p>
            </div>
          </div>
        </div>
      </div>

      {showForm && <CommissionModal onClose={() => setShowForm(false)} startingPrice={s.commission_starting_price || 300} />}
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   ARTIST SECTION
════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════
   ART FOR A CAUSE — Nonprofit Partnership Section
════════════════════════════════════════════════════════ */
function ArtForACauseSection() {
  const [ref, vis] = useVisible(0.12);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ organization_name: "", contact_name: "", contact_email: "", website: "", mission: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization_name.trim() || !form.contact_name.trim() || !form.contact_email.trim() || !form.mission.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/public/charity-apply", form);
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <section ref={ref} className="relative mc-grit overflow-hidden" style={{ background: "#070a07", borderTop: `1px solid rgba(74,222,128,0.08)`, borderBottom: `1px solid rgba(74,222,128,0.08)` }} data-testid="mc-art-for-cause" id="cause">
      {/* Green glow */}
      <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] rounded-full opacity-[0.04] pointer-events-none" style={{ background: "radial-gradient(circle, #4ade80, transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        <div className={`grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-20 items-start transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>

          {/* Left — Message */}
          <div>
            <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-5"
              style={{ fontFamily: F.sans, background: "rgba(74,222,128,0.1)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.2)" }}>
              ART FOR A CAUSE
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[0.95] mb-4"
              style={{ fontFamily: F.cond, color: C.text }}>
              YOUR CAUSE.<br />OUR ART.<br />
              <span style={{ color: "#4ade80" }}>REAL IMPACT.</span>
            </h2>
            <MarkerStroke color="#4ade80" width="60px" height="5px" className="mb-5" />
            <p className="text-sm leading-relaxed mb-4 max-w-md" style={{ fontFamily: F.sans, color: C.text2 }}>
              We donate original marker drawings to nonprofits for raffles and auctions. Then every print sold continues raising money for that organization.
            </p>
            <p className="text-sm leading-relaxed mb-6 max-w-md" style={{ fontFamily: F.sans, color: C.text2 }}>
              It's simple — <strong style={{ color: C.text }}>your cause gets original art to auction off, plus ongoing revenue from print sales.</strong> We handle the art. You handle the mission.
            </p>

            {/* How it works */}
            <div className="space-y-4 mb-8">
              {[
                ["01", "Apply Below", "Tell us about your nonprofit and mission."],
                ["02", "We Create", "A custom marker drawing is made for your cause."],
                ["03", "You Raise", "Auction the original + earn from every print sold."],
              ].map(([num, title, desc]) => (
                <div key={num} className="flex gap-4 items-start">
                  <span className="text-[10px] tracking-[0.2em] font-bold flex-shrink-0 w-8 h-8 flex items-center justify-center"
                    style={{ fontFamily: F.sans, background: "rgba(74,222,128,0.1)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.15)" }}>
                    {num}
                  </span>
                  <div>
                    <p className="text-sm font-bold" style={{ fontFamily: F.sans, color: C.text }}>{title}</p>
                    <p className="text-xs" style={{ fontFamily: F.sans, color: C.text2 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div>
            {!showForm && !submitted ? (
              <div className="p-8 text-center" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.12)" }}>
                <p className="text-lg mb-2 font-bold" style={{ fontFamily: F.hand, color: "#4ade80" }}>
                  "Art should do something bigger than hang on a wall."
                </p>
                <p className="text-xs mb-6" style={{ fontFamily: F.sans, color: C.text2 }}>— Mick</p>
                <button onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-colors"
                  style={{ fontFamily: F.sans, background: "#4ade80", color: C.bg }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#22c55e"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#4ade80"; }}
                  data-testid="mc-cause-apply-btn">
                  APPLY FOR YOUR NONPROFIT
                </button>
                <Link to="/marker-collection/impact" className="block mt-4 text-xs hover:underline" style={{ fontFamily: F.sans, color: "#4ade80" }} data-testid="mc-cause-impact-link">
                  See our impact so far →
                </Link>
              </div>
            ) : submitted ? (
              <div className="p-8 text-center" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
                <div className="text-4xl mb-3" style={{ color: "#4ade80" }}>✓</div>
                <h3 className="text-xl font-black mb-2" style={{ fontFamily: F.cond, color: C.text }}>APPLICATION RECEIVED</h3>
                <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                  We'll review your organization and reach out soon. Thank you for doing good work.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-3" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.12)" }} data-testid="mc-cause-form">
                <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ fontFamily: F.sans, color: "#4ade80" }}>
                  PARTNERSHIP APPLICATION
                </div>
                <input required placeholder="Organization Name *" value={form.organization_name} onChange={e => setForm({...form, organization_name: e.target.value})}
                  className="w-full px-4 py-3 text-sm border focus:outline-none"
                  style={{ fontFamily: F.sans, background: "rgba(0,0,0,0.4)", borderColor: "rgba(74,222,128,0.15)", color: C.text }}
                  data-testid="mc-cause-org-name" />
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Contact Name *" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}
                    className="w-full px-4 py-3 text-sm border focus:outline-none"
                    style={{ fontFamily: F.sans, background: "rgba(0,0,0,0.4)", borderColor: "rgba(74,222,128,0.15)", color: C.text }}
                    data-testid="mc-cause-contact-name" />
                  <input required type="email" placeholder="Email *" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})}
                    className="w-full px-4 py-3 text-sm border focus:outline-none"
                    style={{ fontFamily: F.sans, background: "rgba(0,0,0,0.4)", borderColor: "rgba(74,222,128,0.15)", color: C.text }}
                    data-testid="mc-cause-email" />
                </div>
                <input placeholder="Website (optional)" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
                  className="w-full px-4 py-3 text-sm border focus:outline-none"
                  style={{ fontFamily: F.sans, background: "rgba(0,0,0,0.4)", borderColor: "rgba(74,222,128,0.15)", color: C.text }}
                  data-testid="mc-cause-website" />
                <textarea required placeholder="Tell us about your mission and how this partnership would help *" rows={4} value={form.mission} onChange={e => setForm({...form, mission: e.target.value})}
                  className="w-full px-4 py-3 text-sm border focus:outline-none resize-none"
                  style={{ fontFamily: F.sans, background: "rgba(0,0,0,0.4)", borderColor: "rgba(74,222,128,0.15)", color: C.text }}
                  data-testid="mc-cause-mission" />
                <button type="submit" disabled={submitting}
                  className="w-full px-6 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-colors disabled:opacity-50"
                  style={{ fontFamily: F.sans, background: "#4ade80", color: C.bg }}
                  onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = "#22c55e"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#4ade80"; }}
                  data-testid="mc-cause-submit">
                  {submitting ? "SUBMITTING..." : "SUBMIT APPLICATION"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


function ArtistSection({ settings, aboutEnabled, processEnabled }) {
  const [ref, vis] = useVisible(0.12);
  const s = settings;

  return (
    <section ref={ref} className="relative mc-grit" style={{ background: C.bg }} data-testid="mc-artist">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-[auto_1fr_auto] gap-10 items-center">
          {s.artist_photo && (
            <div className={`transition-all duration-700 ${vis ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
              <div className="w-48 h-48 md:w-60 md:h-60 overflow-hidden flex-shrink-0 relative" style={{ border: `3px solid ${C.bGold}` }}>
                <img src={resolveImage(s.artist_photo)} alt="Mickey Trivett"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                {/* Tape effect */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 -rotate-2 opacity-40"
                  style={{ background: C.paper }} />
              </div>
            </div>
          )}
          <div className={`transition-all duration-700 delay-200 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-4"
              style={{ fontFamily: F.cond, color: C.text }}>
              {s.artist_heading || "MEET THE GUY\nWITH THE MARKER."}
            </h2>
            <MarkerStroke width="50px" className="mb-5" />
            <p className="text-sm leading-relaxed mb-6 max-w-lg" style={{ fontFamily: F.sans, color: C.text2 }}>
              {(s.artist_text || "Mickey Trivett is an East Tennessee artist who turned thousands of marker lines into a pretty good life.").split("\n")[0]}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {aboutEnabled && (
                <Link to="/marker-collection/about" className="inline-flex items-center gap-3 px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5"
                  style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
                  data-testid="mc-read-more-btn">
                  READ MORE <ArrowRight size={14} />
                </Link>
              )}
              {processEnabled && (
                <Link to="/marker-collection/the-process" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold hover:gap-4 transition-all"
                  style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-process-page-link">
                  SEE THE PROCESS <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
          <div className={`hidden lg:block text-right transition-all duration-700 delay-500 ${vis ? "opacity-100" : "opacity-0"}`}>
            <p className="text-lg leading-snug mb-8" style={{ fontFamily: F.hand, color: `${C.gold}50` }}>
              "Same marker.<br/>Different stories.<br/>Always a good time."<br/>— Mick
            </p>
            <div className="text-right">
              <p className="text-2xl font-black tracking-tight leading-tight" style={{ fontFamily: F.cond, color: C.text }}>
                LIVE LIFE<br/>DO GOOD<br/>BE HAPPY
              </p>
              <MarkerStroke width="100px" className="ml-auto mt-2" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   CHALLENGE BOARD CTA
════════════════════════════════════════════════════════ */
function ChallengeBoardCTA() {
  const [ref, vis] = useVisible(0.1);
  const [stats, setStats] = useState({ total: 0, accepted: 0 });

  useEffect(() => {
    http.get("/marker-collection/public/suggestions").then(({ data }) => {
      const total = (data || []).length;
      const accepted = (data || []).filter(s => ["accepted_commission", "accepted_own", "in_queue", "completed"].includes(s.status)).length;
      setStats({ total, accepted });
    }).catch(() => {});
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden mc-grit" style={{ background: C.bg, borderTop: `1px solid ${C.bSub}`, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-challenge-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 sm:gap-8">
          <div className={`flex-1 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 flex items-center justify-center" style={{ background: C.red }}>
                <Flame size={16} color="#fff" />
              </div>
              <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.red }}>
                COMMUNITY CHALLENGE
              </p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[0.9] mb-3"
              style={{ fontFamily: F.cond, color: C.text }}>
              TELL ME WHAT<br/>TO DRAW NEXT.
            </h2>
            <p className="text-sm max-w-md" style={{ fontFamily: F.sans, color: C.text2 }}>
              Submit your wildest ideas. Vote for your favorites. Watch me accept the challenge — or deny it. No hard feelings.
            </p>
            {stats.total > 0 && (
              <div className="flex items-center gap-4 mt-4">
                <span className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: C.gold }}>
                  {stats.total} ideas submitted
                </span>
                {stats.accepted > 0 && (
                  <span className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: "#4ade80" }}>
                    {stats.accepted} challenges accepted
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={`transition-all duration-700 delay-300 ${vis ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <Link to="/marker-collection/challenge-board"
              className="group inline-flex items-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 text-xs sm:text-sm tracking-[0.2em] uppercase font-bold transition-all hover:gap-5"
              style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}
              data-testid="mc-challenge-cta-btn">
              <Flame size={16} /> CHALLENGE ME <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   EMAIL SIGNUP
════════════════════════════════════════════════════════ */
function EmailSection({ settings }) {
  const [ref, vis] = useVisible(0.15);
  const s = settings;

  return (
    <section ref={ref} className="relative overflow-hidden mc-grit" style={{ background: C.bg2 }} data-testid="mc-email">
      <InkSplat className="absolute bottom-[-20px] left-[-30px] w-40 h-40 opacity-[0.03] pointer-events-none" style={{ color: C.cyan }} />
      <div className="max-w-7xl mx-auto px-6 py-14 md:py-18 flex flex-col md:flex-row items-center gap-8 relative z-10">
        <div className={`flex-1 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl sm:text-4xl font-black leading-[0.95] mb-2"
            style={{ fontFamily: F.brush, color: C.gold, textShadow: `0 0 30px ${C.gold}15` }}>
            {s.email_heading || "WANNA SEE\nWHAT I DRAW NEXT?"}
          </h2>
          <p className="text-sm max-w-md" style={{ fontFamily: F.sans, color: C.text2 }}>
            {s.email_text || "First access to new originals, limited prints, and behind-the-scenes studio content."}
          </p>
        </div>
        <form className={`flex flex-1 max-w-md w-full transition-all duration-700 delay-300 ${vis ? "opacity-100" : "opacity-0"}`}
          onSubmit={e => {
            e.preventDefault();
            const em = e.target.elements.email?.value;
            if (em) {
              http.post("/email/subscribe", { email: em, source: "marker_collection" })
                .then(() => { toast.success("You're on the list!"); e.target.reset(); })
                .catch(() => toast.error("Something went wrong"));
            }
          }}>
          <input name="email" type="email" required placeholder="Your email address"
            className="flex-1 px-5 py-3.5 text-sm outline-none"
            style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.bSub}`, color: C.text }}
            data-testid="mc-email-input" />
          <button type="submit" className="px-6 py-3.5 text-xs tracking-[0.2em] uppercase font-bold flex-shrink-0 transition-all duration-300 hover:brightness-110"
            style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
            data-testid="mc-email-submit">
            JOIN <ArrowRight size={14} className="inline ml-1" />
          </button>
        </form>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   MC FOOTER
════════════════════════════════════════════════════════ */
function MCFooter({ pages = {} }) {
  const navLinks = [
    { label: "Gallery", path: "/marker-collection/gallery", enabled: pages?.gallery?.enabled !== false },
    { label: "Originals", path: "/marker-collection/originals", enabled: pages?.originals?.enabled !== false },
    { label: "Commissions", path: "/marker-collection/commissions", enabled: pages?.commissions?.enabled !== false },
    { label: "The Process", path: "/marker-collection/the-process", enabled: pages?.process?.enabled !== false },
    { label: "About", path: "/marker-collection/about", enabled: pages?.about?.enabled !== false },
    { label: "Challenge Board", path: "/marker-collection/challenge-board", enabled: true },
    { label: "Impact", path: "/marker-collection/impact", enabled: true },
  ];
  return (
    <section style={{ background: C.bg, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-footer">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text }}>SIMPLE TRUTHS CLUB</p>
            <p className="text-[9px] tracking-[0.25em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>ORDINARY THINGS. EXTRAORDINARY LESSONS.</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
            {navLinks.filter(l => l.enabled).map(link => (
              <Link key={link.path} to={link.path}
                className="text-[10px] tracking-[0.2em] uppercase font-medium hover:opacity-70 transition-opacity"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-base" style={{ fontFamily: F.hand, color: `${C.gold}50` }}>
            Thanks for being here. — Mick
          </p>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   COMMISSION MODAL
════════════════════════════════════════════════════════ */
const BUDGET_OPTIONS = ["$300 – $500", "$500 – $1,000", "$1,000 – $2,500", "$2,500+", "Let's discuss"];

function CommissionModal({ onClose, startingPrice }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [referenceImages, setReferenceImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (referenceImages.length + files.length > 5) { toast.error("Max 5 images"); return; }
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await http.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setReferenceImages(p => [...p, data.url]);
      } catch { toast.error(`Upload failed`); }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    if (!description.trim()) { toast.error("Describe your vision"); return; }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/public/commission-request", { name, email, description, budget, reference_images: referenceImages });
      setSubmitted(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const inpStyle = { fontFamily: F.sans, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.bSub}`, color: C.text };
  const lblStyle = { fontFamily: F.sans, color: `${C.gold}90` };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="mc-commission-success">
        <div className="w-full max-w-md p-10 text-center shadow-2xl" style={{ background: C.card, border: `1px solid ${C.bSub}` }}>
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ background: C.gold }}>
            <Check size={28} style={{ color: C.bg }} />
          </div>
          <h3 className="text-2xl font-black mb-2" style={{ fontFamily: F.cond, color: C.text }}>Request Received.</h3>
          <p className="text-sm mb-8" style={{ fontFamily: F.sans, color: C.text2 }}>Mickey will review your vision within 48 hours.</p>
          <button onClick={onClose} className="px-8 py-3 text-xs tracking-[0.25em] uppercase font-bold"
            style={{ fontFamily: F.sans, background: C.gold, color: C.bg }} data-testid="mc-commission-done">
            BACK TO COLLECTION
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="mc-commission-modal">
      <div className="w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" style={{ background: C.card, border: `1px solid ${C.bSub}` }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }}>
          <div>
            <h3 className="text-lg font-black" style={{ fontFamily: F.cond, color: C.text }}>Commission Request</h3>
            <p className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.gold }}>Starting at ${Number(startingPrice).toLocaleString()}</p>
          </div>
          <button onClick={onClose} style={{ color: C.text2 }} data-testid="mc-commission-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={lblStyle}>Your Name *</label>
              <input className="w-full px-4 py-2.5 text-sm focus:outline-none" style={inpStyle}
                value={name} onChange={e => setName(e.target.value)} required data-testid="mc-commission-name" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={lblStyle}>Email *</label>
              <input type="email" className="w-full px-4 py-2.5 text-sm focus:outline-none" style={inpStyle}
                value={email} onChange={e => setEmail(e.target.value)} required data-testid="mc-commission-email" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={lblStyle}>What's Your Vision? *</label>
            <textarea className="w-full px-4 py-2.5 text-sm focus:outline-none min-h-[100px] resize-y" style={inpStyle}
              placeholder="A portrait, a scene, a concept..." value={description} onChange={e => setDescription(e.target.value)} required data-testid="mc-commission-desc" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={lblStyle}>Budget Range</label>
            <div className="flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map(opt => (
                <button key={opt} type="button" onClick={() => setBudget(budget === opt ? "" : opt)}
                  className="text-xs px-3 py-1.5 transition-all duration-200"
                  style={{ fontFamily: F.sans, border: `2px solid ${budget === opt ? C.gold : C.bSub}`, background: budget === opt ? `${C.gold}15` : "transparent", color: budget === opt ? C.gold : C.text2 }}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={lblStyle}>Reference Images</label>
            {referenceImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {referenceImages.map((img, i) => (
                  <div key={i} className="relative w-16 h-16 overflow-hidden group" style={{ border: `1px solid ${C.bSub}` }}>
                    <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setReferenceImages(p => p.filter((_, j) => j !== i))}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {referenceImages.length < 5 && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 text-xs border-2 border-dashed px-4 py-3 w-full justify-center transition-colors"
                style={{ fontFamily: F.sans, borderColor: C.bSub, color: C.text2 }} data-testid="mc-commission-upload">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Uploading..." : "Upload Reference Images"}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-4 text-xs tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-300"
            style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
            data-testid="mc-commission-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {submitting ? "SUBMITTING..." : "SUBMIT COMMISSION REQUEST"}
          </button>
        </form>
      </div>
    </div>
  );
}
